import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SUPPORTED_LANGUAGES } from '../../config/languages';

function GlobeIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function LanguageOption({ lang, isSelected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors duration-100 text-left ${isSelected ? 'text-white bg-white/5' : 'text-white/60'}`}
      type="button"
    >
      <span className="text-base flex-shrink-0">{lang.flag}</span>
      <span className="flex-1 font-medium">{lang.nativeLabel}</span>
      <span className="text-white/30 text-xs">{lang.label}</span>
      {isSelected ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0071e3" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : null}
    </button>
  );
}

export default function LanguageSelector({ currentLanguage, onLanguageChange, isTranslating }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handler = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all duration-200 text-sm font-medium ${isTranslating ? 'opacity-60 cursor-wait' : ''}`}
        title="Change Language"
        type="button"
      >
        {isTranslating ? (
          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          <GlobeIcon size={16} />
        )}
        <span className="hidden sm:inline">{currentLanguage === 'en' ? 'Language' : currentLang.nativeLabel}</span>
        {currentLanguage !== 'en' ? <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" /> : null}
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
            className="absolute right-0 top-full mt-2 z-50 w-64 max-h-80 overflow-y-auto bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 py-2"
          >
            <div className="px-3 py-1.5 text-[10px] font-semibold text-white/30 uppercase tracking-widest">Default</div>
            {SUPPORTED_LANGUAGES.filter((l) => l.isDefault).map((lang) => (
              <LanguageOption
                key={lang.code}
                lang={lang}
                isSelected={currentLanguage === lang.code}
                onSelect={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
              />
            ))}

            <div className="px-3 py-1.5 mt-1 text-[10px] font-semibold text-white/30 uppercase tracking-widest border-t border-white/5">Indian Languages</div>
            {SUPPORTED_LANGUAGES.filter((l) => l.flag === '🇮🇳').map((lang) => (
              <LanguageOption
                key={lang.code}
                lang={lang}
                isSelected={currentLanguage === lang.code}
                onSelect={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
              />
            ))}

            <div className="px-3 py-1.5 mt-1 text-[10px] font-semibold text-white/30 uppercase tracking-widest border-t border-white/5">World Languages</div>
            {SUPPORTED_LANGUAGES.filter((l) => l.flag !== '🇮🇳' && !l.isDefault).map((lang) => (
              <LanguageOption
                key={lang.code}
                lang={lang}
                isSelected={currentLanguage === lang.code}
                onSelect={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
              />
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
